import React, { useState } from 'react';
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Line,
	LineChart,
	Area,
	AreaChart,
	Pie,
	PieChart,
	XAxis,
	YAxis
} from 'recharts';
import { Download, Maximize2, X } from 'lucide-react';
import T from 'Components/Helpers/translate';
import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
	type ChartConfig
} from 'App/shadcn/ui/chart';
import { currencyFormat, numberFormat } from 'App/babel/components/Filters/Currencies';
import { CHART_TYPES, type ChartSpec, type ChartType } from './types';

const DONUT_COLORS = [
	'hsl(195 70% 35%)',
	'hsl(35 85% 55%)',
	'hsl(155 50% 40%)',
	'hsl(280 50% 55%)',
	'hsl(15 75% 55%)',
	'hsl(210 60% 50%)',
	'hsl(60 70% 45%)',
	'hsl(330 55% 55%)'
];

function formatValue(n: number, currency?: string): string {
	return currency ? currencyFormat(n, currency) : numberFormat(n);
}

const ALLOWED_CHART_TYPES = new Set<string>(CHART_TYPES);

export function normalizeChartSpec(raw: unknown): ChartSpec {
	const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
	const chartType =
		typeof r.chartType === 'string' && ALLOWED_CHART_TYPES.has(r.chartType)
			? (r.chartType as ChartType)
			: 'table';
	const title = typeof r.title === 'string' && r.title.length > 0 ? r.title : '';
	const rows = Array.isArray(r.rows)
		? (r.rows as unknown[])
				.filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
				.map(row => ({
					key: String(row.key ?? ''),
					value: Number(row.value ?? 0)
				}))
		: [];
	return {
		chartType,
		title,
		rows,
		xLabel: typeof r.xLabel === 'string' ? r.xLabel : undefined,
		yLabel: typeof r.yLabel === 'string' ? r.yLabel : undefined,
		currency: typeof r.currency === 'string' ? r.currency : undefined
	};
}

const ChartBody = ({ spec, height = 320 }: { spec: ChartSpec; height?: number }) => {
	const { chartType, rows, currency, yLabel } = spec;

	if (!rows || rows.length === 0) {
		return <div className="px-1 py-4 text-body-sm text-muted-foreground">{T('default.noData')}</div>;
	}

	if (chartType === 'bigNumber') {
		const total = rows.reduce((sum, r) => sum + (Number(r.value) || 0), 0);
		return (
			<div className="px-2 py-4 flex flex-col gap-2">
				{yLabel && <div className="text-body-sm text-muted-foreground">{yLabel}</div>}
				<div className="text-headline-lg font-semibold text-foreground tabular-nums">
					{formatValue(total, currency)}
				</div>
				{rows.length > 1 && (
					<div className="text-body-sm text-muted-foreground">
						{rows.length} {T('default.rows').toLowerCase()}
					</div>
				)}
			</div>
		);
	}

	if (chartType === 'table') {
		return (
			<div className="px-1 py-2 overflow-x-auto">
				<table className="w-full text-body-sm">
					<thead className="text-muted-foreground">
						<tr className="border-b border-border/40">
							<th className="text-left py-2 font-medium">{spec.xLabel ?? T('default.name')}</th>
							<th className="text-right py-2 font-medium">{yLabel ?? T('default.value')}</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((r, i) => (
							<tr key={`${r.key}-${i}`} className="border-b border-border/20">
								<td className="py-2 text-foreground">{r.key}</td>
								<td className="py-2 text-right tabular-nums text-foreground">
									{formatValue(r.value, currency)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	}

	const config: ChartConfig = {
		value: {
			label: yLabel ?? (T('default.value') as string),
			color: 'hsl(195 70% 35%)'
		}
	};

	if (chartType === 'donut') {
		const donutConfig: ChartConfig = {};
		rows.forEach((r, i) => {
			donutConfig[r.key] = {
				label: r.key,
				color: DONUT_COLORS[i % DONUT_COLORS.length]
			};
		});
		return (
			<div style={{ height }}>
				<ChartContainer config={donutConfig} className="h-full w-full aspect-auto">
					<PieChart>
						<ChartTooltip
							content={
								<ChartTooltipContent
									formatter={value => formatValue(Number(value), currency)}
								/>
							}
						/>
						<Pie data={rows} dataKey="value" nameKey="key" outerRadius={120} innerRadius={70}>
							{rows.map((r, i) => (
								<Cell key={r.key} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
							))}
						</Pie>
					</PieChart>
				</ChartContainer>
			</div>
		);
	}

	const SeriesChart = chartType === 'line' ? LineChart : chartType === 'area' ? AreaChart : BarChart;

	return (
		<div style={{ height }}>
			<ChartContainer config={config} className="h-full w-full aspect-auto">
				<SeriesChart data={rows} margin={{ left: 8, right: 16, top: 16, bottom: 8 }}>
					<CartesianGrid vertical={false} strokeDasharray="3 3" />
					<XAxis dataKey="key" tickLine={false} axisLine={false} fontSize={11} />
					<YAxis
						tickLine={false}
						axisLine={false}
						fontSize={11}
						width={60}
						tickFormatter={v => formatValue(Number(v), currency)}
					/>
					<ChartTooltip
						content={
							<ChartTooltipContent formatter={value => formatValue(Number(value), currency)} />
						}
					/>
					{chartType === 'line' && (
						<Line type="monotone" dataKey="value" stroke="var(--color-value)" strokeWidth={2} dot={false} />
					)}
					{chartType === 'area' && (
						<Area type="monotone" dataKey="value" stroke="var(--color-value)" fill="var(--color-value)" fillOpacity={0.2} />
					)}
					{chartType === 'bar' && <Bar dataKey="value" fill="var(--color-value)" radius={[4, 4, 0, 0]} />}
				</SeriesChart>
			</ChartContainer>
		</div>
	);
};

// CSV-injection guard — mirrors base/src/core/export/generator/excel.js:13-24.
// A cell starting with one of these is quote-prefixed so spreadsheet apps treat
// it as text, not a formula.
const INVALID_START_CHARACTERS = '=+-@';
const csvCell = (raw: string): string => {
	let value = raw;
	if (value && INVALID_START_CHARACTERS.indexOf(value[0]) > -1) {
		value = `'${value}`;
	}
	return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
};

const filename = (title: string): string => {
	const slug = title.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
	return `${slug || 'chart'}.csv`;
};

// Serialize raw rows (numeric values, not locale-formatted) so the download is
// usable in Excel/Sheets, then trigger a client-side download — same Blob →
// objectURL → <a download> pattern as ImageModal.tsx:20-38.
const downloadCsv = (spec: ChartSpec) => {
	const header = [csvCell(spec.xLabel ?? (T('default.name') as string)), csvCell(spec.yLabel ?? (T('default.value') as string))];
	// Label column is text → injection-guarded; value column is a number, so emit
	// it raw — guarding it would prefix negatives ("-5" → "'-5") and Excel would
	// read them as text, breaking the very calculations this download enables.
	// Non-finite values (NaN/Infinity from bad source data) become an empty cell
	// rather than the literal text "NaN", which Excel would treat as a broken value.
	const valueCell = (n: number) => (Number.isFinite(n) ? String(n) : '');
	const lines = [header.join(','), ...spec.rows.map(r => [csvCell(r.key), valueCell(r.value)].join(','))];
	// Prepend a BOM so Excel reads UTF-8 (å/ä/ö) correctly.
	const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
	const objectUrl = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = objectUrl;
	a.download = filename(spec.title);
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(objectUrl);
};

const iconButtonClass =
	'h-7 w-7 rounded-md text-muted-foreground flex items-center justify-center cursor-pointer hover:bg-secondary hover:text-foreground';

const IconButton = ({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) => (
	<button onClick={onClick} type="button" aria-label={label} title={label} className={iconButtonClass}>
		{icon}
	</button>
);

const ChartTitle = ({ spec }: { spec: ChartSpec }) => (
	<div className="flex flex-col gap-0.5 min-w-0">
		<span className="ws-chart-card__eyebrow">{T(`assistant.chart.${spec.chartType}`)}</span>
		{spec.title && <h3 className="ws-chart-card__title">{spec.title}</h3>}
	</div>
);

// Fullscreen view: a larger chart plus the full data table so every value is
// visible. Overlay styling follows ImageModal.tsx.
const ChartModal = ({ spec, onClose }: { spec: ChartSpec; onClose: () => void }) => {
	const showTable = spec.chartType !== 'table' && spec.chartType !== 'bigNumber' && spec.rows.length > 0;
	return (
		<div
			className="fixed inset-0 z-[10020] bg-foreground/75 backdrop-blur-sm flex items-center justify-center cursor-pointer"
			onClick={onClose}
		>
			<div
				className="relative bg-background rounded-xl shadow-lg w-[min(90vw,900px)] max-h-[90vh] overflow-y-auto cursor-default"
				onClick={e => e.stopPropagation()}
			>
				<div className="flex items-start justify-between gap-4 px-6 pt-5 pb-2">
					<ChartTitle spec={spec} />
					<div className="flex gap-1 shrink-0">
						<IconButton
							icon={<Download className="h-4 w-4" />}
							label={T('assistant.exportCsv')}
							onClick={() => downloadCsv(spec)}
						/>
						<IconButton icon={<X className="h-4 w-4" />} label={T('assistant.close')} onClick={onClose} />
					</div>
				</div>
				<div className="px-6 pb-5">
					<ChartBody spec={spec} height={Math.round(window.innerHeight * 0.5)} />
					{showTable && <ChartBody spec={{ ...spec, chartType: 'table' }} />}
				</div>
			</div>
		</div>
	);
};

const CARD_STYLES = `
.ws-chart-card {
	width: 100%;
	border: 1px solid rgba(0,0,0,0.06);
	border-radius: 14px;
	background: var(--ws-panel, #fbfbfa);
	box-shadow: 0 1px 2px rgba(0,0,0,0.03), 0 4px 12px rgba(0,0,0,0.03);
	overflow: hidden;
	display: flex;
	flex-direction: column;
}
.ws-chart-card__header {
	padding: 14px 18px 6px;
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
}
.ws-chart-card__eyebrow {
	font-size: 11px;
	font-weight: 500;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: var(--ws-ink-4, #8b929a);
}
.ws-chart-card__title {
	font-size: 15px;
	font-weight: 600;
	line-height: 1.3;
	color: var(--ws-ink, #1d252e);
	margin: 0;
}
.ws-chart-card__body {
	padding: 6px 18px 16px;
}
`;

const ChartCard = ({ spec }: { spec: ChartSpec }) => {
	const [expanded, setExpanded] = useState(false);
	return (
		<>
			<style>{CARD_STYLES}</style>
			<div className="ws-chart-card">
				<div className="ws-chart-card__header">
					<ChartTitle spec={spec} />
					{spec.rows.length > 0 && (
						<div className="flex gap-1 shrink-0">
							<IconButton
								icon={<Maximize2 className="h-4 w-4" />}
								label={T('assistant.expand')}
								onClick={() => setExpanded(true)}
							/>
							<IconButton
								icon={<Download className="h-4 w-4" />}
								label={T('assistant.exportCsv')}
								onClick={() => downloadCsv(spec)}
							/>
						</div>
					)}
				</div>
				<div className="ws-chart-card__body">
					<ChartBody spec={spec} />
				</div>
			</div>
			{expanded && <ChartModal spec={spec} onClose={() => setExpanded(false)} />}
		</>
	);
};

export default ChartCard;
